import { FC, useState, useCallback, ChangeEvent, useEffect } from 'react';
import styled from 'styled-components';
import { useForm, SubmitHandler, UseFormSetValue } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Link, useNavigate } from 'react-router';

import { AUTH_ROUTES } from '@navigation/types';
import { useSignUpMutation } from '@redux/queries/auth';
import { useAuthUser } from '@redux/selectors/auth';
import {
  email,
  password,
  confirmPassword,
  agreeWithTS,
} from '@utils/constants/schemas';
import { TERMS_OR_SERVICE_URL } from '@utils/constants';
import {
  TEXT_INPUT_LABELS,
  TEXT_INPUT_PLACEHOLDERS,
  TEXT_INPUT_ICONS,
} from '@utils/constants/form';
import theme from 'theme';
import { Checkbox } from '@components/fields/checkbox';
import { DefaultText } from '@components/text';
import TextInput from '@components/fields/input';
import PasswordInput from '@components/fields/input/PasswordInput';

import {
  StyledContainer,
  StyledFormContainer,
  StyledForm,
  StyledButton,
  StyledErrorContainer,
  StyledErrorText,
} from './styled';

const CheckboxWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const StyledLink = styled(Link)`
  font-size: ${({ theme }) => theme.fonts.fontSize.base};
  color: ${({ theme }) => theme.colors.purple[300]};
  text-decoration: none;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
`;

type SignUpState = {
  email: string;
  password: string;
  confirmPassword: string;
  agreeWithTS: boolean;
};

const defaultValues: SignUpState = {
  email: '',
  password: '',
  confirmPassword: '',
  agreeWithTS: false,
};

const schema = yup.object().shape({
  email,
  password,
  confirmPassword,
  agreeWithTS,
});

export const SignUpForm: FC = () => {
  const [signUp, { isSuccess, isLoading, isError }] = useSignUpMutation();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { user: authUser, isAnonymous } = useAuthUser();
  const {
    register,
    handleSubmit,
    clearErrors,
    setValue,
    reset: resetForm,
    watch,
    formState: { errors },
  } = useForm<SignUpState>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });
  const navigate = useNavigate();

  const handleChange = useCallback(
    ({
      field,
      text,
      onChange,
    }: {
      field: keyof SignUpState;
      text: string | boolean;
      onChange: UseFormSetValue<SignUpState>;
    }) => {
      clearErrors(field);
      onChange(field, text);

      if (errorMessage) {
        setErrorMessage('');
      }
    },
    [clearErrors, errorMessage, setErrorMessage],
  );

  const onSubmit: SubmitHandler<SignUpState> = (data: SignUpState) => {
    if (data) {
      // eslint-disable-next-line
      const { agreeWithTS, ...rest } = data;
      signUp(rest);
    }
    resetForm({}, { keepValues: true, keepDirty: false });
  };

  useEffect(() => {
    if (isSuccess && authUser?.id && !isAnonymous) {
      navigate(AUTH_ROUTES.CONFIRM_EMAIL, { replace: true });
    }
  }, [isSuccess, navigate, authUser?.id, isAnonymous]);

  useEffect(() => {
    if (isError) {
      setErrorMessage('Invalid email or password');
    }
  }, [isError]);

  useEffect(() => {
    if (errors.agreeWithTS?.message) {
      setErrorMessage(errors.agreeWithTS?.message);
    }
  }, [errors.agreeWithTS?.message]);

  return (
    <StyledContainer>
      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        <StyledFormContainer>
          <TextInput
            placeholder={TEXT_INPUT_PLACEHOLDERS.EMAIL}
            label={TEXT_INPUT_LABELS.EMAIL}
            icon={TEXT_INPUT_ICONS.EMAIL}
            errorText={errors.email?.message}
            {...register('email')}
            value={watch('email')}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleChange({
                field: 'email',
                text: e.target.value,
                onChange: setValue,
              })
            }
            disabled={isLoading}
          />

          <PasswordInput
            placeholder={TEXT_INPUT_PLACEHOLDERS.PASSWORD}
            label={TEXT_INPUT_LABELS.PASSWORD}
            errorText={errors.password?.message}
            {...register('password')}
            value={watch('password')}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleChange({
                field: 'password',
                text: e.target.value,
                onChange: setValue,
              })
            }
            disabled={isLoading}
          />

          <PasswordInput
            placeholder={TEXT_INPUT_PLACEHOLDERS.CONFIRM_PASSWORD}
            label={TEXT_INPUT_LABELS.CONFIRM_PASSWORD}
            errorText={errors.confirmPassword?.message}
            {...register('confirmPassword')}
            value={watch('confirmPassword')}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleChange({
                field: 'confirmPassword',
                text: e.target.value,
                onChange: setValue,
              })
            }
            disabled={isLoading}
          />

          <CheckboxWrapper>
            <Checkbox
              {...register('agreeWithTS')}
              checked={watch('agreeWithTS')}
              onChange={(e) => {
                handleChange({
                  field: 'agreeWithTS',
                  text: e.target.checked,
                  onChange: setValue,
                });
              }}
              disabled={isLoading}
            />

            <DefaultText>Agree to </DefaultText>

            <StyledLink
              to={TERMS_OR_SERVICE_URL}
              target='_blank'
              rel='noopener noreferrer'
            >
              Terms and Conditions
            </StyledLink>
          </CheckboxWrapper>
        </StyledFormContainer>

        <StyledErrorContainer>
          {errorMessage && <StyledErrorText>{errorMessage}</StyledErrorText>}
        </StyledErrorContainer>

        <StyledButton
          disabled={isLoading || !watch('email') || !watch('password')}
          isLoading={isLoading}
          type='submit'
          textStyle={{ fontSize: theme.fonts.fontSize.xl }}
        >
          Sign Up
        </StyledButton>
      </StyledForm>
    </StyledContainer>
  );
};
